// Service Types Management Page
import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import SortableTable from '../components/SortableTable';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { clearServiceTypesCache } from '../utils/serviceTypeUtils';
import { format } from 'date-fns';

function ServiceTypes() {
  const { user } = useAuth();
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rate_type: 'FLAT',
    flat_completion_rate: '',
    flat_refusal_rate: '',
    hourly_rate: '',
    description: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Table column definitions
  const headCells = [
    {
      id: 'name',
      label: 'Service Type',
      align: 'left',
      sx: { minWidth: '200px' }
    },
    { id: 'code', label: 'Code', align: 'left' },
    { id: 'rate_type', label: 'Billing Type', align: 'center' },
    { id: 'rates', label: 'Rates', align: 'left', disableSorting: true },
    { id: 'is_active', label: 'Status', align: 'center' },
    { 
      id: 'actions', 
      label: 'Actions', 
      align: 'center', 
      disableSorting: true,
      sx: { minWidth: '120px' }
    },
  ];

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  async function fetchServiceTypes() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_types')
        .select('*')
        .order('name');
        
      if (error) {
        throw error;
      }
      
      if (data) {
        setServiceTypes(data);
      }
    } catch (error) {
      console.error('Error fetching service types:', error);
      setSnackbar({
        open: true,
        message: `Error fetching service types: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }

  const handleAddNew = () => {
    setEditingServiceType(null);
    setFormData({
      name: '',
      code: '',
      rate_type: 'FLAT',
      flat_completion_rate: '',
      flat_refusal_rate: '',
      hourly_rate: '',
      description: ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleEdit = (serviceType) => {
    // Prevent editing Initial Service Call rates
    if (serviceType.code === 'INITIAL') {
      setSnackbar({
        open: true,
        message: 'Initial Service Call rates cannot be modified',
        severity: 'warning'
      });
      return;
    }

    setEditingServiceType(serviceType);
    setFormData({
      name: serviceType.name,
      code: serviceType.code,
      rate_type: serviceType.rate_type,
      flat_completion_rate: serviceType.flat_completion_rate || '',
      flat_refusal_rate: serviceType.flat_refusal_rate || '',
      hourly_rate: serviceType.hourly_rate || '',
      description: serviceType.description || ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleToggleActive = async (serviceType) => {
    // Prevent deactivating Initial Service Call
    if (serviceType.code === 'INITIAL' && serviceType.is_active) {
      setSnackbar({
        open: true,
        message: 'Initial Service Call cannot be deactivated',
        severity: 'warning'
      });
      return;
    }

    try {
      const newStatus = !serviceType.is_active;
      const { error } = await supabase
        .from('service_types')
        .update({ is_active: newStatus })
        .eq('id', serviceType.id);

      if (error) throw error;

      // Update local state
      setServiceTypes(serviceTypes.map(st => 
        st.id === serviceType.id ? { ...st, is_active: newStatus } : st
      ));

      // Clear cache to ensure fresh data
      clearServiceTypesCache();

      setSnackbar({
        open: true,
        message: `Service type ${newStatus ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling service type:', error);
      setSnackbar({
        open: true,
        message: `Error updating service type: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleViewHistory = async (serviceType) => {
    try {
      const { data, error } = await supabase
        .from('service_type_audit')
        .select('*')
        .eq('service_type_id', serviceType.id)
        .order('changed_at', { ascending: false });

      if (error) throw error;

      setAuditHistory(data || []);
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching audit history:', error);
      setSnackbar({
        open: true,
        message: `Error fetching history: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Service type name is required';
    }

    if (!formData.code.trim()) {
      errors.code = 'Service type code is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.code)) {
      errors.code = 'Code must be uppercase letters, numbers, and underscores only';
    }

    if (formData.rate_type === 'FLAT') {
      if (!formData.flat_completion_rate || parseFloat(formData.flat_completion_rate) <= 0) {
        errors.flat_completion_rate = 'Completion rate must be greater than 0';
      }
      if (formData.flat_refusal_rate === '' || parseFloat(formData.flat_refusal_rate) < 0) {
        errors.flat_refusal_rate = 'Refusal rate must be 0 or greater';
      }
    } else if (formData.rate_type === 'HOURLY') {
      if (!formData.hourly_rate || parseFloat(formData.hourly_rate) <= 0) {
        errors.hourly_rate = 'Hourly rate must be greater than 0';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const serviceTypeData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        rate_type: formData.rate_type,
        flat_completion_rate: formData.rate_type === 'FLAT' ? parseFloat(formData.flat_completion_rate) : null,
        flat_refusal_rate: formData.rate_type === 'FLAT' ? parseFloat(formData.flat_refusal_rate) : null,
        hourly_rate: formData.rate_type === 'HOURLY' ? parseFloat(formData.hourly_rate) : null,
        description: formData.description.trim()
      };

      let result;
      let enrollmentInfo = null;
      
      if (editingServiceType) {
        // Update existing - use backend API
        const { data: session } = await supabase.auth.getSession();
        const token = session?.session?.access_token;
        
        const response = await fetch(`/api/service-types/${editingServiceType.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(serviceTypeData)
        });
        
        const responseData = await response.json();
        if (!response.ok || !responseData.success) {
          throw new Error(responseData.message || 'Failed to update service type');
        }
        
        result = responseData.serviceType;

        // Update local state
        setServiceTypes(serviceTypes.map(st => 
          st.id === editingServiceType.id ? result : st
        ));
      } else {
        // Create new - use backend API to get auto-enrollment info
        const { data: session } = await supabase.auth.getSession();
        const token = session?.session?.access_token;
        
        const response = await fetch('/api/service-types', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(serviceTypeData)
        });
        
        const responseData = await response.json();
        if (!response.ok || !responseData.success) {
          throw new Error(responseData.message || 'Failed to create service type');
        }
        
        result = responseData.serviceType;
        enrollmentInfo = responseData.autoEnrollment;

        // Update local state
        setServiceTypes([...serviceTypes, result]);
      }

      // Clear cache to ensure fresh data
      clearServiceTypesCache();

      setDialogOpen(false);
      
      // Show success message with enrollment info if creating
      const message = editingServiceType 
        ? 'Service type updated successfully'
        : enrollmentInfo && enrollmentInfo.providersEnrolled > 0
          ? `Service type created successfully! Automatically enrolled ${enrollmentInfo.providersEnrolled} provider${enrollmentInfo.providersEnrolled !== 1 ? 's' : ''} with default rates.`
          : 'Service type created successfully';
      
      setSnackbar({
        open: true,
        message,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving service type:', error);
      setSnackbar({
        open: true,
        message: `Error saving service type: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatRates = (serviceType) => {
    if (serviceType.rate_type === 'HOURLY') {
      return `$${serviceType.hourly_rate}/hour`;
    } else {
      return `$${serviceType.flat_completion_rate}/$${serviceType.flat_refusal_rate}`;
    }
  };

  const renderRow = (row) => (
    <TableRow key={row.id}>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {row.name}
          </Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary">
              {row.description}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontFamily="monospace">
          {row.code}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip 
          label={row.rate_type} 
          size="small"
          color={row.rate_type === 'HOURLY' ? 'secondary' : 'primary'}
        />
      </TableCell>
      <TableCell>{formatRates(row)}</TableCell>
      <TableCell align="center">
        <Chip 
          label={row.is_active ? 'Active' : 'Inactive'} 
          size="small"
          color={row.is_active ? 'success' : 'default'}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title={row.code === 'INITIAL' ? 'Cannot edit Initial Service Call' : 'Edit service type'}>
            <span>
              <IconButton 
                onClick={() => handleEdit(row)} 
                color="primary" 
                size="small"
                disabled={row.code === 'INITIAL'}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={row.is_active ? 'Deactivate' : 'Activate'}>
            <span>
              <IconButton 
                onClick={() => handleToggleActive(row)} 
                color={row.is_active ? 'error' : 'success'} 
                size="small"
                disabled={row.code === 'INITIAL' && row.is_active}
              >
                {row.is_active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View history">
            <IconButton 
              onClick={() => handleViewHistory(row)} 
              color="default" 
              size="small"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Service Types
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Add Service Type
          </Button>
        </Box>
        
        <Typography paragraph>
          Manage different Coca-Cola project types and their billing rates. Service types determine how contractor work is calculated and billed.
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Note:</strong> The "Initial Service Call" rates ($300 completion / $50 refusal) are locked and cannot be modified to ensure backwards compatibility.
        </Alert>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SortableTable
            rows={serviceTypes}
            headCells={headCells}
            defaultOrderBy="name"
            tableId="service-types-table"
            defaultRowsPerPage={25}
            renderRow={renderRow}
            tableProps={{ sx: { minWidth: 650 }, size: "small", "aria-label": "service types table" }}
            tableContainerProps={{ 
              component: Paper, 
              sx: { mt: 3, overflow: 'auto', maxWidth: '100%' } 
            }}
            emptyContent={
              <Alert severity="info">No service types found. This should not happen - check database connection.</Alert>
            }
          />
        )}
        
        {/* Add/Edit Dialog */}
        <Dialog 
          open={dialogOpen} 
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingServiceType ? 'Edit Service Type' : 'Add New Service Type'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Service Type Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
              
              <TextField
                fullWidth
                label="Service Type Code"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value.toUpperCase())}
                error={!!formErrors.code}
                helperText={formErrors.code || 'Uppercase letters, numbers, and underscores only'}
                required
                disabled={!!editingServiceType}
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />

              <FormControl fullWidth required>
                <InputLabel>Billing Type</InputLabel>
                <Select
                  value={formData.rate_type}
                  onChange={(e) => handleFormChange('rate_type', e.target.value)}
                  label="Billing Type"
                  disabled={!!editingServiceType}
                >
                  <MenuItem value="FLAT">Flat Rate (per location)</MenuItem>
                  <MenuItem value="HOURLY">Hourly Rate</MenuItem>
                </Select>
              </FormControl>

              {!editingServiceType && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  New service type will automatically be added to all providers who have 
                  default {formData.rate_type === 'HOURLY' ? 'hourly' : 'flat'} rates configured.
                </Alert>
              )}

              {formData.rate_type === 'FLAT' && (
                <>
                  <TextField
                    fullWidth
                    label="Completion Rate"
                    type="number"
                    value={formData.flat_completion_rate}
                    onChange={(e) => handleFormChange('flat_completion_rate', e.target.value)}
                    error={!!formErrors.flat_completion_rate}
                    helperText={formErrors.flat_completion_rate || 'Rate paid for completed work'}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label="Refusal Rate"
                    type="number"
                    value={formData.flat_refusal_rate}
                    onChange={(e) => handleFormChange('flat_refusal_rate', e.target.value)}
                    error={!!formErrors.flat_refusal_rate}
                    helperText={formErrors.flat_refusal_rate || 'Rate paid for refused/cancelled work'}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </>
              )}

              {formData.rate_type === 'HOURLY' && (
                <TextField
                  fullWidth
                  label="Hourly Rate"
                  type="number"
                  value={formData.hourly_rate}
                  onChange={(e) => handleFormChange('hourly_rate', e.target.value)}
                  error={!!formErrors.hourly_rate}
                  helperText={formErrors.hourly_rate || 'Rate paid per billable hour'}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    endAdornment: <InputAdornment position="end">/hour</InputAdornment>,
                  }}
                />
              )}

              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={2}
                helperText="Optional description for this service type"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary">
              {editingServiceType ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* History Dialog */}
        <Dialog 
          open={historyDialogOpen} 
          onClose={() => setHistoryDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Change History</DialogTitle>
          <DialogContent>
            {auditHistory.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>No changes recorded for this service type.</Alert>
            ) : (
              <SortableTable
                rows={auditHistory}
                headCells={[
                  { id: 'changed_at', label: 'Date/Time', align: 'left' },
                  { id: 'changed_by', label: 'Changed By', align: 'left' },
                  { id: 'action', label: 'Action', align: 'center' },
                  { id: 'changes', label: 'Changes', align: 'left', disableSorting: true },
                ]}
                defaultOrderBy="changed_at"
                defaultOrder="desc"
                tableId="audit-history-table"
                defaultRowsPerPage={10}
                renderRow={(row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {format(new Date(row.changed_at), 'MM/dd/yyyy h:mm a')}
                    </TableCell>
                    <TableCell>{row.changed_by || 'System'}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={row.action} 
                        size="small"
                        color={row.action === 'INSERT' ? 'success' : row.action === 'DELETE' ? 'error' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                        {JSON.stringify(row.changes, null, 2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

export default ServiceTypes; 